import { useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Loader2, Layers } from "lucide-react";

const TILES = {
  street: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap" },
  satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" },
};
const homeIcon = () => L.divIcon({ className: "", iconSize: [30, 30], iconAnchor: [15, 30],
  html: `<div style="width:26px;height:26px;background:#E68A2E;border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>` });

function ClickCatcher({ onPick }) {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

export default function HomePicker({ m, open, onClose, initial, onDone }) {
  const [pt, setPt] = useState(initial?.lat ? { lat: initial.lat, lng: initial.lng } : null);
  const [mapType, setMapType] = useState("street");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!pt) return toast.error("Tap the map to drop your home pin");
    setSaving(true);
    try {
      const { data } = await api.post("/me/home", { lat: pt.lat, lng: pt.lng });
      onDone(data);
      toast.success(`Home base set to ${data.town}`);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const DLG = m ? "bg-[#161B22] text-slate-100 border border-white/10 max-w-xl" : "bg-white text-[#14181F] border border-[#E4E7EB] max-w-xl";
  const center = pt ? [pt.lat, pt.lng] : (initial?.lat ? [initial.lat, initial.lng] : [52, -108]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DLG} data-testid="home-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Pick your home on the map</DialogTitle>
          <DialogDescription>Tap anywhere to drop your home pin — the map and planner measure from here.</DialogDescription>
        </DialogHeader>

        <div className="relative h-[46vh] min-h-[280px] rounded-lg overflow-hidden border border-black/10">
          <button type="button" onClick={() => setMapType((t) => t === "street" ? "satellite" : "street")} data-testid="home-picker-maptype"
            className="absolute top-2.5 right-2.5 z-[1000] flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-white text-slate-700 shadow-md border border-black/10">
            <Layers className="w-3.5 h-3.5" /> {mapType === "street" ? "Satellite" : "Map"}
          </button>
          <MapContainer center={center} zoom={pt || initial?.lat ? 8 : 4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer url={TILES[mapType].url} attribution={TILES[mapType].attribution} />
            {mapType === "satellite" && <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />}
            <ClickCatcher onPick={setPt} />
            {pt && <Marker position={[pt.lat, pt.lng]} icon={homeIcon()} />}
          </MapContainer>
        </div>

        <div className="text-xs text-slate-400 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-[#E68A2E]" />
          {pt ? `Pinned at ${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}` : "No point picked yet"}
        </div>

        <DialogFooter>
          <button onClick={onClose} className="text-sm font-semibold px-4 py-2.5 rounded-lg text-slate-400 hover:text-slate-200">Cancel</button>
          <button onClick={save} disabled={saving || !pt} data-testid="home-picker-save"
            className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Set home here
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
